/**
 * Admin Session Service - API calls for managing access sessions
 * Requires Google OAuth token for authorization.
 */

import { getAccessToken } from './authService';

const API_BASE = '/api/admin-sessions';

/**
 * Get auth headers with Bearer token.
 * @returns {Promise<HeadersInit>}
 */
async function getAuthHeaders() {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please authenticate first.');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * List all sessions.
 * @returns {Promise<{sessions: Array}>}
 */
export async function listSessions() {
  const headers = await getAuthHeaders();
  const response = await fetch(API_BASE, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to list sessions');
  }
  return response.json();
}

/**
 * Create a new session with optional scope restrictions.
 * @param {number} ttlMinutes - Time to live in minutes (1 to 525600)
 * @param {string} label - Human-readable label
 * @param {object} options - Optional scope configuration
 * @param {string} options.scope_type - 'all', 'districts', 'properties', or 'mixed'
 * @param {string[]} options.scope_districts - Array of district names to restrict to
 * @param {string[]} options.scope_properties - Array of property IDs to restrict to
 * @param {string} options.landing_path - Custom landing page path (e.g. '/', '/housing/abc123')
 * @returns {Promise<{id: string, token: string, access_url: string, expires_at: string}>}
 */
export async function createSession(ttlMinutes, label = '', { scope_type = 'all', scope_districts = [], scope_properties = [], landing_path = '/' } = {}) {
  const headers = await getAuthHeaders();
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ttl_minutes: ttlMinutes,
      label,
      scope_type,
      scope_districts,
      scope_properties,
      landing_path,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to create session');
  }
  return response.json();
}

/**
 * Fetch available districts and properties for session scoping.
 * @returns {Promise<{districts: Array, properties: Array}>}
 */
export async function fetchSessionOptions() {
  const headers = await getAuthHeaders();
  const response = await fetch('/api/admin-session-options', { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to fetch session options');
  }
  return response.json();
}

/**
 * Revoke (soft-delete) a session.
 * @param {string} sessionId
 * @returns {Promise<{success: boolean}>}
 */
export async function revokeSession(sessionId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}?id=${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to revoke session');
  }
  return response.json();
}

/**
 * Renew a session - reset its expiration with a new TTL.
 * @param {string} sessionId
 * @param {number} ttlMinutes - New TTL in minutes (default 60)
 * @returns {Promise<{success: boolean, id: string, expires_at: string}>}
 */
export async function renewSession(sessionId, ttlMinutes = 60) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}?id=${encodeURIComponent(sessionId)}&ttl=${ttlMinutes}`, {
    method: 'PUT',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to renew session');
  }
  return response.json();
}

/**
 * Permanently delete a session from the database.
 * @param {string} sessionId
 * @returns {Promise<{success: boolean}>}
 */
export async function hardDeleteSession(sessionId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}?id=${encodeURIComponent(sessionId)}&action=hard`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Failed to delete session');
  }
  return response.json();
}

const AdminSessionService = {
  listSessions,
  createSession,
  revokeSession,
  renewSession,
  hardDeleteSession,
};

export default AdminSessionService;
